import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
} from "@fluentui/react-components";
import { PeopleAdd32Regular } from "@fluentui/react-icons";
import { AppSpinner } from "components/AppSpinner/AppSpinner";
import CheckboxField from "components/controls/CheckboxField";
import DropzoneComponent from "components/DropzoneComponent/DropzoneComponent";
import { PrimaryButton } from "components/PrimaryButton/PrimaryButton";
import { ActionButton } from "components/styled";
import { useRoleType } from "hooks/useRoleType";
import { useAtomValue } from "jotai";
import { QUERY_KEYS } from "library/shared";
import { IFile } from "library/types";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useImportEmployeesFromExcel } from "state/queries/useImportEmployeesFromExcel";
import { selectedCompanyState } from "store/UIHrPage";

export function ImportEmployeeTemplate() {
  const { t } = useTranslation();
  const selectedCompany = useAtomValue(selectedCompanyState);
  const isReadOnlyUser = useRoleType()?.hasHrReadOnly;

  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [sendInvitationEmail, setSendInvitationEmail] = useState(true);
  const [employeeTemplate, setEmployeeTemplate] = useState<IFile | null>(null);

  const { mutate: onImportEmployeeTemplate, isLoading: importInProgress } =
    useImportEmployeesFromExcel();

  const handleSetEmployeeTemplate = useCallback((file: IFile) => {
    setEmployeeTemplate(file);
  }, []);

  const clearCacheDataForEmployeeAndDependent = () => {
    queryClient.removeQueries({
      queryKey: [QUERY_KEYS.employeeEnrollment],
    });
    queryClient.removeQueries({
      queryKey: [QUERY_KEYS.dependentEnrollment],
    });
    queryClient.removeQueries({
      queryKey: [QUERY_KEYS.employeeDetails],
    });
    queryClient.removeQueries({
      queryKey: [QUERY_KEYS.dependentDetails],
    });
  };
  return (
    <>
      <ActionButton
        noBorder
        appearance="subtle"
        disabled={isReadOnlyUser}
        icon={<PeopleAdd32Regular />}
        onClick={() => {
          setOpen(true);
        }}
      >
        {t("hbh.importEmployeeTemplate.label")}
      </ActionButton>
      {open && (
        <Dialog modalType="alert" open={open}>
          <DialogSurface>
            {importInProgress && <AppSpinner />}
            <DialogBody>
              <DialogTitle
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                {t("hbh.importFromExcelTemplate.label")}
              </DialogTitle>
              <DialogContent>
                <DropzoneComponent setFile={handleSetEmployeeTemplate} />
                <CheckboxField
                  name="sendInvitationEmail"
                  required
                  value={sendInvitationEmail}
                  setFieldValue={(_, value) => {
                    setSendInvitationEmail(value);
                  }}
                  label={t("hbh.sendInvitationMail.label")}
                />
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button
                    appearance="secondary"
                    onClick={() => {
                      setOpen(false);
                    }}
                  >
                    {t("greco.cancel")}
                  </Button>
                </DialogTrigger>
                <PrimaryButton
                  disabled={importInProgress}
                  onClick={() => {
                    if (!employeeTemplate) return;
                    onImportEmployeeTemplate(
                      {
                        clientInternalNumberGOS:
                          Number(selectedCompany?.clientInternalNumberGos) || 0,
                        countryCode: Number(selectedCompany?.countryCode) || 0,
                        payload: {
                          ...employeeTemplate,
                          sendInvitationEmail,
                        },
                      },
                      {
                        onSuccess: () => {
                          setOpen(false);
                          clearCacheDataForEmployeeAndDependent();
                          toast.success(
                            t("hbh.employeeTemplateImported.label")
                          );
                        },
                      }
                    );
                  }}
                >
                  {t("hbh.import.label")}
                </PrimaryButton>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </>
  );
}
